import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { Badge } from '../Badge';
import { ChevronDownIcon } from '../../Icons';
import { forwardRef } from 'react';
import { keyframes, styled } from '../../system';
import { wrapWithDefaultProps } from '../../lib/wrap-with-default-props';
export const itemUpShow = keyframes({
    '0%': { h: 0 },
    '100%': { h: 'var(--radix-accordion-content-height)' }
});
export const itemDownHide = keyframes({
    '0%': { h: 'var(--radix-accordion-content-height)' },
    '100%': { h: 0 }
});
const StyledAccordion = styled(AccordionPrimitive.Root, {
    borderRadius: '$lg',
    border: '1px solid $borderColor',
    divideY: '1px solid $borderColor'
});
const StyledItem = styled(AccordionPrimitive.Item, {
    overflow: 'hidden',
    '&:first-child': {
        mt: 0,
        borderTopLeftRadius: '$lg',
        borderTopRightRadius: '$lg'
    },
    '&:last-child': {
        borderBottomLeftRadius: '$lg',
        borderBottomRightRadius: '$lg'
    }
});
const StyledHeader = styled(AccordionPrimitive.Header, {
    display: 'flex'
});
const StyledTrigger = styled(AccordionPrimitive.Trigger, {
    d: 'flex',
    bgColor: 'transparent',
    minH: 56,
    flex: 1,
    flexCenter: true,
    fontWeight: '$medium',
    cursor: 'pointer',
    justifyContent: 'space-between',
    color: '$text',
    '&[data-state="closed"]': { bgColor: '$surface' },
    '&[data-state="open"]': { bgColor: '$surface' },
    '@hover': {
        '&:hover': { bgColor: '$hoverBg' },
        '@dark': {
            '&:hover': {
                bgColor: '$hoverBg'
            }
        }
    },
    variants: {
        size: {
            md: {
                py: '$2',
                px: '$4',
                textMd: true
            },
            lg: {
                py: '$6',
                px: '$6',
                textLg: true
            }
        }
    },
    defaultVariants: {
        size: 'md'
    }
});
const StyledContent = styled(AccordionPrimitive.Content, {
    overflow: 'hidden',
    color: '$text',
    bgColor: '$surface',
    transition: 'height 250ms ease-in-out',
    '&[data-state="open"]': {
        animation: `${itemUpShow} 300ms cubic-bezier(0.87, 0, 0.13, 1)`
    },
    '&[data-state="closed"]': {
        animation: `${itemDownHide} 300ms cubic-bezier(0.87, 0, 0.13, 1)`
    }
});
export const Accordion = wrapWithDefaultProps(StyledAccordion, {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    collapsible: 'true'
});
export const AccordionItem = StyledItem;
export const AccordionTrigger = forwardRef(({ children, hideIcon, ...props }, ref) => (_jsx(StyledHeader, { children: _jsxs(StyledTrigger, { ...props, ref: ref, children: [children, hideIcon ? null : (_jsx(Badge, { css: {
                    minMaxSize: 28,
                    bgColor: '$primary50',
                    color: '$primary500',
                    transition: 'transform 300ms cubic-bezier(0.87, 0, 0.13, 1)',
                    '[data-state=open] > &': { transform: 'rotate(180deg)' },
                    '@dark': {
                        bgColor: '$primary900',
                        color: '$primary400'
                    }
                }, "aria-hidden": true, children: _jsx(ChevronDownIcon, { width: "9", height: "6" }) }))] }) })));
export const AccordionContent = forwardRef(({ children, ...props }, ref) => (_jsx(StyledContent, { ...props, ref: ref, children: children })));
AccordionTrigger.displayName = 'AccordionTrigger';
AccordionContent.displayName = 'AccordionContent';
